import { type FormEvent, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CardCvcElement,
  CardExpiryElement,
  CardNumberElement,
  Elements,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import type { Stripe } from '@stripe/stripe-js';
import {
  SUBSCRIBE_PAYMENT_RESULT_PATH,
  type SubscribeBillingPeriod,
} from '@/lib/stripePaymentResult';
import { userFacingError } from '@/lib/userFacingError';

export interface SubscribePaymentSession {
  clientSecret: string;
  orderId: number | string;
  paymentIntentId: string;
}

const CARD_ELEMENT_OPTIONS = {
  style: {
    base: {
      fontSize: '14px',
      color: '#1f2937',
      fontFamily: 'system-ui, -apple-system, sans-serif',
      '::placeholder': { color: '#9ca3af' },
    },
    invalid: { color: '#dc2626' },
  },
};

export interface StripeCustomCardFormProps {
  stripePromise: Promise<Stripe | null>;
  session: SubscribePaymentSession;
  amountLabel: string;
  billing: SubscribeBillingPeriod;
  onError: (message: string) => void;
}

function StripeCardFormInner({
  session,
  amountLabel,
  billing,
  onError,
}: Omit<StripeCustomCardFormProps, 'stripePromise'>) {
  const navigate = useNavigate();
  const stripe = useStripe();
  const elements = useElements();
  const [cardholderName, setCardholderName] = useState('');
  const [processing, setProcessing] = useState(false);
  const [fieldError, setFieldError] = useState<string | null>(null);

  const formReady = Boolean(stripe && elements);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!stripe || !elements) {
      onError('Payment is not ready. Please try again.');
      return;
    }

    const cardNumber = elements.getElement(CardNumberElement);
    if (!cardNumber) {
      onError('Card form is not ready. Please try again.');
      return;
    }

    const name = cardholderName.trim();
    if (!name) {
      setFieldError('Please enter the cardholder name.');
      return;
    }

    setProcessing(true);
    onError('');
    setFieldError(null);

    try {
      const { error, paymentIntent } = await stripe.confirmCardPayment(session.clientSecret, {
        payment_method: {
          card: cardNumber,
          billing_details: { name },
        },
      });

      if (error) {
        throw new Error(error.message ?? 'Card payment failed.');
      }

      const paymentIntentId = paymentIntent?.id ?? session.paymentIntentId;
      const status = paymentIntent?.status ?? 'unknown';

      navigate(SUBSCRIBE_PAYMENT_RESULT_PATH, {
        replace: true,
        state: {
          stripe: {
            orderId: session.orderId,
            paymentIntentId,
            status,
          },
          billing,
          amountLabel,
        },
      });
    } catch (err) {
      onError(userFacingError(err, 'Payment failed. Please try again.'));
      setProcessing(false);
    }
  };

  return (
    <form onSubmit={(e) => void handleSubmit(e)}>
      <div className="border border-gray-200 rounded-lg p-3 mb-4 space-y-3">
        <div>
          <label htmlFor="cardholder-name" className="block text-xs font-normal text-gray-600 mb-1">
            Cardholder name
          </label>
          <input
            id="cardholder-name"
            type="text"
            autoComplete="cc-name"
            value={cardholderName}
            onChange={(ev) => setCardholderName(ev.target.value)}
            placeholder="Name on card"
            className="w-full rounded-lg border border-gray-200 px-3 py-2.5 text-sm font-normal text-gray-800 placeholder:font-normal placeholder:text-xs placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-green-500/40 focus:border-green-500"
          />
        </div>

        <div>
          <label className="block text-xs font-normal text-gray-600 mb-1">Card number</label>
          <div className="rounded-lg border border-gray-200 px-3 py-2.5 bg-white min-h-[42px] [&_iframe]:!h-[22px]">
            <CardNumberElement
              options={{
                ...CARD_ELEMENT_OPTIONS,
                showIcon: true,
                disableLink: true,
              }}
              onChange={(ev) => setFieldError(ev.error?.message ?? null)}
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-normal text-gray-600 mb-1">Expiry</label>
            <div className="rounded-lg border border-gray-200 px-3 py-2.5 bg-white min-h-[42px]">
              <CardExpiryElement options={CARD_ELEMENT_OPTIONS} />
            </div>
          </div>
          <div>
            <label className="block text-xs font-normal text-gray-600 mb-1">CVC</label>
            <div className="rounded-lg border border-gray-200 px-3 py-2.5 bg-white min-h-[42px]">
              <CardCvcElement options={CARD_ELEMENT_OPTIONS} />
            </div>
          </div>
        </div>

        {fieldError && (
          <p className="text-xs text-red-600" role="alert">
            {fieldError}
          </p>
        )}

        <p className="text-gray-400 text-[10px] leading-relaxed">
          Card details are processed securely by Stripe. We never store your full card number.
        </p>
      </div>

      <button
        type="submit"
        disabled={processing || !formReady}
        className="w-full h-11 rounded-lg bg-green-500 text-white font-normal text-sm cursor-pointer whitespace-nowrap hover:bg-green-600 transition-colors disabled:opacity-70 flex items-center justify-center gap-2"
      >
        {processing ? (
          <>
            <i className="ri-loader-4-line animate-spin text-base" />
            Processing...
          </>
        ) : (
          <>
            <i className="ri-shield-check-line text-base" />
            Confirm & Pay {amountLabel}
          </>
        )}
      </button>
    </form>
  );
}

export function StripeCustomCardForm({
  stripePromise,
  session,
  amountLabel,
  billing,
  onError,
}: StripeCustomCardFormProps) {
  // Card Element（CardNumberElement 等）不要传 clientSecret；
  // clientSecret 仅用于 confirmCardPayment。传给 Elements 会进入 Payment Element 模式，导致卡号框无法输入。
  return (
    <Elements
      key={session.clientSecret}
      stripe={stripePromise}
      options={{ locale: 'en' }}
    >
      <StripeCardFormInner
        session={session}
        amountLabel={amountLabel}
        billing={billing}
        onError={onError}
      />
    </Elements>
  );
}
